import { Extension } from "@tiptap/core"

export const FONT_SIZE_PRESETS = [
  "12px",
  "14px",
  "16px",
  "18px",
  "20px",
  "24px",
  "30px",
  "36px",
  "48px",
  "64px",
  "72px",
  "96px",
]

export function normalizeFontSize(value) {
  if (value == null) return null

  const normalizedValue = String(value).trim().toLowerCase()

  if (!normalizedValue) {
    return null
  }

  if (/^\d+(\.\d+)?$/.test(normalizedValue)) {
    return `${normalizedValue}px`
  }

  if (/^\d+(\.\d+)?(px|pt|em|rem|%)$/.test(normalizedValue)) {
    return normalizedValue
  }

  return null
}

function compactAttrs(attributes) {
  return Object.fromEntries(
    Object.entries(attributes).filter(([, value]) => value != null && value !== ""))
}

function updateStyleProperty(styleValue, propertyName, nextValue = null) {
  const declarations = String(styleValue || "")
    .split(";")
    .map((declaration) => declaration.trim())
    .filter(Boolean)
    .filter((declaration) => declaration.split(":")[0]?.trim().toLowerCase() !== propertyName)

  if (nextValue) {
    declarations.push(`${propertyName}: ${nextValue}`)
  }

  return declarations.length > 0 ? declarations.join("; ") : null
}

function buildTextStyleAttrs(existingAttributes = {}, fontSize = null) {
  const nextAttributes = { ...existingAttributes }

  if (fontSize) {
    nextAttributes.fontSize = fontSize
  } else {
    delete nextAttributes.fontSize
  }

  return compactAttrs(nextAttributes)
}

function buildSpanAttrs(existingAttributes = {}, fontSize = null) {
  const nextAttributes = { ...existingAttributes }

  nextAttributes.style = updateStyleProperty(existingAttributes.style, "font-size", fontSize)

  return compactAttrs(nextAttributes)
}

function updateStoredFontSizeMark(state, tr, textStyleType, fontSize) {
  const currentMarks = state.storedMarks || state.selection.$from.marks()
  const textStyleMark = currentMarks.find((mark) => mark.type === textStyleType)
  const nextAttributes = buildTextStyleAttrs(textStyleMark?.attrs, fontSize)

  tr.removeStoredMark(textStyleType)

  if (Object.keys(nextAttributes).length > 0) {
    tr.addStoredMark(textStyleType.create(nextAttributes))
  }

  return tr
}

function updateStoredSpanFontSizeMark(state, tr, spanType, fontSize) {
  const currentMarks = state.storedMarks || state.selection.$from.marks()
  const spanMark = currentMarks.find((mark) => mark.type === spanType)

  if (!spanMark) {
    return tr
  }

  const nextAttributes = buildSpanAttrs(spanMark.attrs, fontSize)

  tr.removeStoredMark(spanType)

  if (Object.keys(nextAttributes).length > 0) {
    tr.addStoredMark(spanType.create(nextAttributes))
  }

  return tr
}

function updateSelectionFontSizeMark(state, tr, textStyleType, fontSize) {
  const { from, to } = state.selection
  const segments = []

  state.doc.nodesBetween(from, to, (node, pos) => {
    if (!node.isText) {
      return
    }

    const segmentFrom = Math.max(pos, from)
    const segmentTo = Math.min(pos + node.nodeSize, to)

    if (segmentFrom >= segmentTo) {
      return
    }

    const textStyleMark = node.marks.find((mark) => mark.type === textStyleType)

    segments.push({
      from: segmentFrom,
      to: segmentTo,
      attrs: textStyleMark?.attrs || {},
    })
  })

  segments.forEach((segment) => {
    tr.removeMark(segment.from, segment.to, textStyleType)

    const nextAttributes = buildTextStyleAttrs(segment.attrs, fontSize)

    if (Object.keys(nextAttributes).length > 0) {
      tr.addMark(segment.from, segment.to, textStyleType.create(nextAttributes))
    }
  })

  return tr
}

function updateSelectionSpanFontSizeMark(state, tr, spanType, fontSize) {
  const { from, to } = state.selection
  const segments = []

  state.doc.nodesBetween(from, to, (node, pos) => {
    if (!node.isText) {
      return
    }

    const segmentFrom = Math.max(pos, from)
    const segmentTo = Math.min(pos + node.nodeSize, to)

    if (segmentFrom >= segmentTo) {
      return
    }

    const spanMark = node.marks.find((mark) => mark.type === spanType)

    if (!spanMark) {
      return
    }

    segments.push({
      from: segmentFrom,
      to: segmentTo,
      attrs: spanMark.attrs || {},
    })
  })

  segments.forEach((segment) => {
    tr.removeMark(segment.from, segment.to, spanType)

    const nextAttributes = buildSpanAttrs(segment.attrs, fontSize)

    if (Object.keys(nextAttributes).length > 0) {
      tr.addMark(segment.from, segment.to, spanType.create(nextAttributes))
    }
  })

  return tr
}

export const FontSize = Extension.create({
  name: "fontSize",

  addOptions() {
    return {
      types: ["textStyle"],
    }
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => normalizeFontSize(element.style.fontSize),
            renderHTML: (attributes) => {
              if (!attributes.fontSize) {
                return {}
              }

              return {
                style: `font-size: ${attributes.fontSize}`,
              }
            },
          },
        },
      },
    ]
  },

  addCommands() {
    return {
      setFontSize: (fontSize) => ({ state, tr, dispatch }) => {
        const normalizedFontSize = normalizeFontSize(fontSize)
        const textStyleType = state.schema.marks.textStyle
        const spanType = state.schema.marks.span

        if (!normalizedFontSize || !textStyleType) {
          return false
        }

        if (!dispatch) {
          return true
        }

        let transaction = state.selection.empty
          ? updateStoredFontSizeMark(state, tr, textStyleType, normalizedFontSize)
          : updateSelectionFontSizeMark(state, tr, textStyleType, normalizedFontSize)

        if (spanType) {
          transaction = state.selection.empty
            ? updateStoredSpanFontSizeMark(state, transaction, spanType, normalizedFontSize)
            : updateSelectionSpanFontSizeMark(state, transaction, spanType, normalizedFontSize)
        }

        dispatch(transaction)
        return true
      },

      unsetFontSize: () => ({ state, tr, dispatch }) => {
        const textStyleType = state.schema.marks.textStyle
        const spanType = state.schema.marks.span

        if (!textStyleType) {
          return false
        }

        if (!dispatch) {
          return true
        }

        let transaction = state.selection.empty
          ? updateStoredFontSizeMark(state, tr, textStyleType, null)
          : updateSelectionFontSizeMark(state, tr, textStyleType, null)

        if (spanType) {
          transaction = state.selection.empty
            ? updateStoredSpanFontSizeMark(state, transaction, spanType, null)
            : updateSelectionSpanFontSizeMark(state, transaction, spanType, null)
        }

        dispatch(transaction)
        return true
      },
    }
  },
})

export default FontSize
